import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ComposedChart, ReferenceLine,
} from "recharts";

const COL_LOAD = "#3B82F6";
const COL_POWER = "#60A5FA";
const COL_UTIL = "#1E3A8A";
const COL_COST = "#EC4899";
const COL_KWH = "#1E3A8A";
const COL_CO2 = "#3B82F6";
const COL_PEAK = "#3B82F6";
const COL_DECL = "#1E3A8A";

const T = ({ active, payload, label, unit = "", digits = 2 }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E5DE] p-3 font-mono-data text-xs shadow-sm">
      <div className="text-[#1A1B1A] font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex justify-between gap-6" style={{ color: p.color }}>
          <span>{p.name}</span>
          <span>
            {typeof p.value === "number" ? p.value.toFixed(digits) : p.value}
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
};

function StatTile({ label, value, unit = "", testid }) {
  return (
    <div data-testid={testid} className="flat-card p-5">
      <div className="overline mb-1">{label}</div>
      <div className="font-display text-3xl sm:text-4xl text-[#0F3524] tracking-tight">
        {value}
        {unit && <span className="text-base sm:text-lg text-[#6E716C] ml-1 font-mono-data">{unit}</span>}
      </div>
    </div>
  );
}

function fmt(n, digits = 2) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-MY", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
function pct(n, digits = 0) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

export default function PerformanceKPIs({ months, kpis }) {
  if (!months?.length) return null;

  const hasPeak = !!kpis.has_peak_data;
  const hasPF = !!kpis.has_power_factor_data;
  const hasUtil = !!kpis.has_utilization;
  const hasExport = !!kpis.has_export_data;

  const data = months.map((m) => ({
    month: m.month_label,
    kwh: m.kwh,
    kwh_exported: m.kwh_exported,
    cost_per_kwh: m.cost_per_kwh,
    load_factor: m.load_factor,
    power_factor: m.power_factor,
    utilization: m.utilization,
    peak_demand: m.peak_demand_kw,
    declared_load: kpis.declared_load_kw || 0,
    co2: m.co2_kg,
  }));

  return (
    <section data-testid="performance-kpis" className="space-y-6">
      <div className="flex items-baseline justify-between border-b border-[#E5E5DE] pb-3">
        <div>
          <div className="overline mb-1">Section</div>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Performance &amp; KPIs</h2>
        </div>
        <div className="text-xs font-mono-data text-[#6E716C]">
          {months.length} months on file
        </div>
      </div>

      {/* Averages row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatTile
          testid="perf-kpi-cost-per-kwh"
          label="Avg cost / kWh"
          value={`RM ${fmt(kpis.avg_cost_per_kwh, 4)}`}
        />
        <StatTile
          testid="perf-kpi-load-factor"
          label="Avg load factor"
          value={pct(kpis.avg_load_factor)}
        />
        {hasPF && (
          <StatTile
            testid="perf-kpi-power-factor"
            label="Avg power factor"
            value={pct(kpis.avg_power_factor)}
          />
        )}
        {hasUtil && (
          <StatTile
            testid="perf-kpi-utilization"
            label="Avg utilization"
            value={pct(kpis.avg_utilization)}
          />
        )}
        <StatTile
          testid="perf-kpi-imported"
          label="Energy imported"
          value={fmt(kpis.total_kwh, 0)}
          unit="kWh"
        />
        {hasExport && (
          <StatTile
            testid="perf-kpi-exported"
            label="Energy exported"
            value={fmt(kpis.total_kwh_exported, 0)}
            unit="kWh"
          />
        )}
      </div>

      {/* Chart grid — all 3 in one row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Load Factor Trend Over Time (with utilization area) */}
        <div data-testid="perf-chart-lf-trend" className="flat-card p-6 sm:p-8">
          <div className="overline mb-2">Chart</div>
          <h3 className="font-display text-xl tracking-tight mb-4">Load Factor Trend Over Time</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#EDEDE8" vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
              <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} width={44} domain={[0, 1]} />
              <Tooltip content={<T digits={3} />} />
              <Legend iconType="square" wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <Area type="monotone" name="Load Factor" dataKey="load_factor" stroke={COL_LOAD} fill={COL_LOAD} fillOpacity={0.25} strokeWidth={2} />
              {hasUtil && (
                <Area type="monotone" name="Utilization" dataKey="utilization" stroke={COL_UTIL} fill={COL_UTIL} fillOpacity={0.2} strokeWidth={2} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Power Factor Over Time — only when data */}
        {hasPF && (
          <div data-testid="perf-chart-pf" className="flat-card p-6 sm:p-8">
            <div className="overline mb-2">Chart</div>
            <h3 className="font-display text-xl tracking-tight mb-4">Power Factor Over Time</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#EDEDE8" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} width={44} domain={[0.5, 1]} />
                <Tooltip content={<T digits={3} />} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Area type="monotone" name="Power Factor" dataKey="power_factor" stroke={COL_POWER} fill={COL_POWER} fillOpacity={0.3} strokeWidth={2} />
                <ReferenceLine y={0.85} stroke="#B24F35" strokeDasharray="3 3" label={{ value: "0.85 target", position: "right", fontSize: 10, fill: "#B24F35" }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Peak Demand vs Declared Load — only when peak data */}
        {hasPeak && (
          <div data-testid="perf-chart-peak-decl" className="flat-card p-6 sm:p-8">
            <div className="overline mb-2">Chart</div>
            <h3 className="font-display text-xl tracking-tight mb-4">Peak Demand vs Declared Load</h3>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#EDEDE8" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                <YAxis tickLine={false} axisLine={false} width={50} />
                <Tooltip content={<T unit=" kW" digits={2} />} />
                <Legend iconType="line" wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Bar dataKey="peak_demand" name="Peak Demand" fill={COL_PEAK} barSize={24} />
                {(kpis.declared_load_kw || 0) > 0 && (
                  <Line
                    type="monotone"
                    dataKey="declared_load"
                    name="Declared Load"
                    stroke={COL_DECL}
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="4 4"
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
}
